import { creatorbase, creatorbaseQueries } from '@round/api';
import { Skeleton } from '@round/ui-kit';
import { useMemo } from 'react';
import { omit } from 'lodash';
import * as yup from 'yup';
import { Form, FormikProvider, useFormik, yupToFormErrors } from 'formik';
import {
    Button,
    ExternalMultiValuesControl,
    FormField,
    Modal,
    None,
    SelectField,
    StaticTextValue,
} from '@round/whitelabel-ui';
import { usePartnership, useUsers } from '@round/creatorbase-helpers/api-hooks';
import { UserOption, mapUserToOption, useUsersMultiSelect } from 'Modules/Plans/User/hooks/useUsersSelect';
import { getObjectDifference, showNotification } from 'helpers';
import { mapApiErrorsToFormikErrors } from 'utility/utility';
import useUpdateReceivedCampaign from './useUpdateReceivedCampaign';
import { CampaignTableRow } from '../ProjectCampaignsTable/ProjectCampaignsTable';
import styles from './EditReceivedCampaignModal.module.css';

type FormValues = {
    name: string;
    team_members: UserOption[];
};

const mapToApi = (values: FormValues): creatorbase.PatchCampaignShareBody => ({
    name: values.name,
    planner_ids: values.team_members.map((user) => user.value),
});

const validationSchema = yup.object({
    name: yup.string().required('Name is required'),
});

type Props = {
    isOpen: boolean;
    onClose: () => void;
    campaign: CampaignTableRow | null;
};

const EditReceivedCampaignModal = ({ isOpen, onClose, campaign }: Props) => {
    const { update } = useUpdateReceivedCampaign(campaign);
    const { data: partnership } = usePartnership(campaign?.run_by_partnership_id, { enabled: isOpen });

    const teamMembers = campaign?.team_members;
    const { data: users, isLoading: arePlannersLoading } = useUsers(
        { id: teamMembers?.join(), page_size: teamMembers?.length },
        { enabled: !!teamMembers?.length && isOpen }
    );

    const initialValues = useMemo<FormValues>(
        () => ({
            name: campaign?.name ?? '',
            team_members: users?.results.map(mapUserToOption) ?? [],
        }),
        [campaign?.name, users?.results]
    );

    const formik = useFormik<FormValues>({
        initialValues,
        enableReinitialize: true,
        validate: async (values) => {
            try {
                await validationSchema.validate(values, { abortEarly: false });
            } catch (e) {
                return yupToFormErrors(e);
            }
        },
        onSubmit: (values, helpers) => {
            const campaignShareId = campaign?.campaign_share_id;
            if (typeof campaignShareId !== 'number') {
                helpers.setSubmitting(false);
                return;
            }

            // planner_ids replaces the whole planner set, so send only the fields that changed
            const changedFields = getObjectDifference(mapToApi(values), mapToApi(initialValues));

            update(
                { id: campaignShareId, ...changedFields },
                {
                    onSuccess: () => {
                        showNotification('Campaign updated', 'info');
                        helpers.resetForm();
                        onClose();
                    },
                    onError: (error) => {
                        if (
                            creatorbaseQueries.campaignShares.utils.update.isExpectedErrorResponse(error) &&
                            error.response.status === 400
                        ) {
                            helpers.setErrors(
                                mapApiErrorsToFormikErrors({
                                    name: error.response.data.name,
                                    team_members: error.response.data.planner_ids,
                                })
                            );
                            return;
                        }

                        showNotification('Could not update campaign', 'error');
                    },
                    onSettled: () => helpers.setSubmitting(false),
                }
            );
        },
    });

    const { getProps: getTeamMembersProps } = useUsersMultiSelect({
        initOn: 'menuOpen',
        params: { team_id: partnership?.client_team_id },
        enabled: !!partnership,
    });

    const handleClose = () => {
        formik.resetForm();
        onClose();
    };

    return (
        <Modal className={styles.modal} closeOnOverlayClick isOpen={isOpen} onClose={handleClose}>
            <Modal.Header>
                <Modal.Title>Edit campaign</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <FormikProvider value={formik}>
                    <Form className={styles.form}>
                        <FormField
                            name="name"
                            label="Campaign name"
                            placeholder="Enter a campaign name"
                            disabled={arePlannersLoading}
                        />

                        {arePlannersLoading ? (
                            <Skeleton height="4rem" />
                        ) : (
                            <SelectField
                                name="team_members"
                                label="Planners"
                                placeholder="Select planners"
                                isClearable={false}
                                controlShouldRenderValue={false}
                                {...omit(getTeamMembersProps(), ['value', 'onChange'])}
                                components={{
                                    Control: ExternalMultiValuesControl,
                                    Input: StaticTextValue,
                                    MultiValue: None,
                                    Placeholder: None,
                                }}
                            />
                        )}
                    </Form>
                </FormikProvider>
            </Modal.Body>
            <Modal.Actions>
                <Button
                    appearance="primary"
                    disabled={!formik.dirty || !formik.isValid}
                    isLoading={formik.isSubmitting}
                    onClick={formik.submitForm}
                >
                    Submit
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default EditReceivedCampaignModal;
